import { omit } from 'lodash'

import type { ApiClient } from 'clients/yogi-api'
import type {
  AlertBody,
  AlertResponse,
  AlertType,
} from 'features/alerts/service/types'
import {
  dropNullFilterFields,
  type ServerSelectedFilterOption,
} from 'features/filters/utils'
import type { SharedUsersResponse } from 'features/share/types'
import type { UserInfo } from 'types/project'

const CLIENT_ALERTS_BASE_URL = '/client/alerts'
const HUB_UUID_HEADER = 'X-Hub-UUID'

// The v1 filter fields. `filters_v2` is the only filter shape a persisting
// request may carry: a loaded `AlertType` still holds these next to it, so every
// caller that spreads one into a save/update (duplicate, archive, delete, edit)
// would otherwise re-send a stale v1 snapshot. Stripped once here, at the
// boundary, rather than trusted to each caller.
const LEGACY_FILTER_KEYS = [
  'filters',
  'search_terms',
  'search_criteria',
  'search_terms_logical',
] as const

type ApiAlertType = Omit<
  AlertType,
  'calc_info' | 'created_by' | 'error_message' | 'proj_uuid' | 'filters_v2'
> & {
  // Always populated by the backend (computed from legacy columns as a
  // fallback for pre-migration rows) — required, unlike the legacy fields
  // below which the backend may still return as null for very old rows.
  // Each filter's optional fields (`min`, `max`, ...) arrive as `null`, not
  // absent — `toAlertResponse` drops them.
  filters_v2: Omit<AlertType['filters_v2'], 'filters'> & {
    filters: ServerSelectedFilterOption[]
  }
  created_by_email: string
  created_by_full_name: string
  created_by_id: number
  filters: AlertType['filters'] | null
  project_uuid: string
  reviews_filter: number | null
  search_criteria: string[] | null
  search_terms: string[] | null
}

type ApiAlertResponse = {
  alert: ApiAlertType
  owners?: UserInfo[]
  viewers?: UserInfo[]
  is_enabled: boolean
}

const toProjectConfig = (
  proj_uuid: string,
): { headers: Record<string, string> } => ({
  headers: {
    [HUB_UUID_HEADER]: proj_uuid,
  },
})

const toAlertResponse = (response: ApiAlertResponse): AlertResponse => {
  const createdBy = {
    email: response.alert.created_by_email,
    full_name: response.alert.created_by_full_name,
    id: response.alert.created_by_id,
  }
  const { project_uuid, ...alert } = response.alert

  return {
    alert: {
      ...alert,
      calc_info: '',
      created_by: createdBy,
      error_message: '',
      filters: alert.filters ?? [],
      filters_v2: dropNullFilterFields(alert.filters_v2),
      proj_uuid: project_uuid,
      reviews_filter: alert.reviews_filter ?? 0,
      search_criteria: alert.search_criteria ?? [],
      search_terms: alert.search_terms ?? [],
    },
    owners: response.owners ?? [],
    viewers: response.viewers ?? [],
    is_enabled: response.is_enabled,
  }
}

export class AlertService {
  constructor(private client: ApiClient) {}

  async getAlerts(proj_uuid: string): Promise<AlertResponse[]> {
    const { data } = await this.client.get<ApiAlertResponse[]>(
      CLIENT_ALERTS_BASE_URL,
      toProjectConfig(proj_uuid),
    )

    return data.map(toAlertResponse)
  }

  async fetchSharedUsers(proj_uuid: string): Promise<SharedUsersResponse> {
    const { data } = await this.client.get<SharedUsersResponse>(
      '/project/shared',
      {
        params: {
          proj_uuid,
        },
      },
    )
    return data
  }

  async saveAlert(proj_uuid: string, alert: AlertBody): Promise<AlertResponse> {
    const { data } = await this.client.post<ApiAlertResponse>(
      CLIENT_ALERTS_BASE_URL,
      omit(alert, LEGACY_FILTER_KEYS),
      toProjectConfig(proj_uuid),
    )
    return toAlertResponse(data)
  }

  async updateAlert(
    proj_uuid: string,
    alert: AlertType,
  ): Promise<AlertResponse> {
    const { data } = await this.client.put<ApiAlertResponse>(
      `${CLIENT_ALERTS_BASE_URL}/${alert.id}`,
      omit(alert, LEGACY_FILTER_KEYS),
      toProjectConfig(proj_uuid),
    )
    return toAlertResponse(data)
  }

  async enableAlert(proj_uuid: string, alertId: number): Promise<void> {
    await this.client.put(
      `${CLIENT_ALERTS_BASE_URL}/${alertId}`,
      { enabled_for_caller: true },
      toProjectConfig(proj_uuid),
    )
  }

  async disableAlert(proj_uuid: string, alertId: number): Promise<void> {
    await this.client.put(
      `${CLIENT_ALERTS_BASE_URL}/${alertId}`,
      { enabled_for_caller: false },
      toProjectConfig(proj_uuid),
    )
  }

  async shareAlert(
    proj_uuid: string,
    body: { alert_id: number; as_owners: boolean; email_addresses: string[] },
  ): Promise<void> {
    await this.client.post(
      `${CLIENT_ALERTS_BASE_URL}/${body.alert_id}/recipients`,
      {
        as_owners: body.as_owners,
        email_addresses: body.email_addresses,
      },
      toProjectConfig(proj_uuid),
    )
  }

  async unshareAlert(
    proj_uuid: string,
    body: { alert_id: number; email_addresses: string[] },
  ): Promise<void> {
    await this.client.delete(
      `${CLIENT_ALERTS_BASE_URL}/${body.alert_id}/recipients`,
      {
        ...toProjectConfig(proj_uuid),
        data: {
          email_addresses: body.email_addresses,
        },
      },
    )
  }

  async unsubscribeAlert(
    proj_uuid: string,
    alert_id: number,
    email_address: string,
  ): Promise<void> {
    await this.client.delete(
      `${CLIENT_ALERTS_BASE_URL}/${alert_id}/recipients`,
      {
        ...toProjectConfig(proj_uuid),
        data: {
          email_addresses: [email_address],
        },
      },
    )
  }

  async getAlertChart(proj_uuid: string, alert: AlertBody): Promise<unknown> {
    const { data } = await this.client.post(
      `${CLIENT_ALERTS_BASE_URL}/preview`,
      {
        ...alert,
      },
      toProjectConfig(proj_uuid),
    )
    return data
  }
}
